import { colorStrings, mediaQueriesBP } from "@iewcasia/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { History, useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import X from "@insite/mobius/Icons/X";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    desktopBackgroundImage = "desktopBackgroundImage",
    mobileBackgroundImage = "mobileBackgroundImage",
    imageAltText = "imageAltText",
    hotspots = "hotspots",
    hotspotLabel = "hotspotLabel",
    hotspotColor = "hotspotColor",
    positionXPercent = "positionXPercent",
    positionYPercent = "positionYPercent",
    title = "title",
    description = "description",
    contentImage = "contentImage",
    ctaText = "ctaText",
    ctaLink = "ctaLink",
    ctaDisplayType = "ctaDisplayType",
    contentImageSize = "contentImageSize",
    openInNewTab = "openInNewTab",
}

export type CtaDisplayType = "button" | "link";
export type ContentImageSize = "small" | "medium" | "large";

interface HotspotModel {
    fields: {
        [fields.hotspotLabel]: string;
        [fields.hotspotColor]: string;
        [fields.positionXPercent]: number | null;
        [fields.positionYPercent]: number | null;
        [fields.title]: string;
        [fields.description]: string;
        [fields.contentImage]: string;
        [fields.contentImageSize]: ContentImageSize;
        [fields.ctaText]: string;
        [fields.ctaLink]: LinkFieldValue;
        [fields.ctaDisplayType]: CtaDisplayType;
        [fields.openInNewTab]: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.desktopBackgroundImage]: string;
        [fields.mobileBackgroundImage]: string;
        [fields.imageAltText]: string;
        [fields.hotspots]: HotspotModel[];
    };
}

export type Props = OwnProps;

/** Normalizes ImagePicker / CMS paths (e.g. strips trailing ";", commas, whitespace). */
export const sanitizeCmsImageSrc = (src: string | undefined | null): string => {
    if (src == null || typeof src !== "string") {
        return "";
    }
    return src.trim().replace(/[\s;,:]+$/g, "");
};

export interface InteractivePhotoMapWidgetStyles {
    root?: InjectableCss;
    imageStage?: InjectableCss;
    desktopImage?: InjectableCss;
    mobileImage?: InjectableCss;
    singleBackgroundImage?: InjectableCss;
    hotspotLabelTypography?: TypographyProps;
    backdrop?: InjectableCss;
    detailPanel?: InjectableCss;
    detailPanelInner?: InjectableCss;
    detailCloseButton?: ButtonProps;
    detailImageWrapper?: InjectableCss;
    detailBody?: InjectableCss;
    detailTitle?: TypographyProps;
    detailDescription?: TypographyProps;
    detailCta?: ButtonProps;
    detailCtaLink?: LinkPresentationProps;
    mobileList?: InjectableCss;
    mobileListItem?: InjectableCss;
    mobileListItemActive?: InjectableCss;
    mobileListHeader?: InjectableCss;
    mobileListContent?: InjectableCss;
    mobileListNumber?: TypographyProps;
    mobileListTitle?: TypographyProps;
    mobileListDescription?: TypographyProps;
    mobileListImage?: InjectableCss;
    mobileListCta?: ButtonProps;
    mobileListCtaLink?: LinkPresentationProps;
}

export const interactivePhotoMapWidgetStyles: InteractivePhotoMapWidgetStyles = {
    root: {
        css: css`
            width: 100%;
            margin: 0 auto;
        `,
    },
    imageStage: {
        css: css`
            position: relative;
            width: 100%;
            line-height: 0;
            overflow: hidden;
            border-radius: 4px;
            background-color: ${getColor(colorStrings.commonGlobalGray04)};
        `,
    },
    desktopImage: {
        css: css`
            display: block;
            width: 100%;
            height: auto;
            line-height: 0;

            img {
                width: 100%;
                height: auto;
                display: block;
                vertical-align: top;
            }

            ${mediaQueriesBP.maxLG} {
                display: none;
            }
        `,
    },
    mobileImage: {
        css: css`
            display: none;
            width: 100%;
            height: auto;
            line-height: 0;

            img {
                width: 100%;
                height: auto;
                display: block;
                vertical-align: top;
            }

            ${mediaQueriesBP.maxLG} {
                display: block;
            }
        `,
    },
    singleBackgroundImage: {
        css: css`
            display: block;
            width: 100%;
            line-height: 0;

            img {
                width: 100%;
                height: auto;
                display: block;
                vertical-align: top;
            }
        `,
    },
    hotspotLabelTypography: {
        size: 14,
        weight: 700,
        css: css`
            margin: 0;
            line-height: 1;
            color: ${getColor(colorStrings.commonGlobalWhite)};
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
        `,
    },
    backdrop: {
        css: css`
            display: none;
            position: fixed;
            inset: 0;
            z-index: 999;
            background: rgba(0, 0, 0, 0.35);
            border: 0;
            padding: 0;
            cursor: pointer;

            ${mediaQueriesBP.minLG} {
                display: block;
            }
        `,
    },
    detailPanel: {
        css: css`
            display: none;
            position: absolute;
            z-index: 1001;
            width: fit-content;
            box-sizing: border-box;
            pointer-events: auto;
            overflow: visible;

            ${mediaQueriesBP.minLG} {
                display: block;
            }
        `,
    },
    detailPanelInner: {
        css: css`
            position: relative;
            width: 100%;
            box-sizing: border-box;
            background: ${getColor(colorStrings.commonGlobalWhite)};
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
            overflow: hidden;

            ${mediaQueriesBP.minLG} {
                display: flex;
                flex-direction: row;
                align-items: stretch;
            }
        `,
    },
    detailCloseButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            position: absolute;
            top: 8px;
            right: 8px;
            z-index: 2;
            min-width: 36px;
            min-height: 36px;
            padding: 0;
            border: 0;
            background: transparent;
        `,
    },
    detailImageWrapper: {
        css: css`
            flex: 0 0 auto;
            align-self: stretch;
            background: ${getColor(colorStrings.commonGlobalGray04)};

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }

            ${mediaQueriesBP.maxLG} {
                display: none;
            }
        `,
    },
    detailBody: {
        css: css`
            flex: 1 1 0;
            min-width: 0;
            padding: 24px 40px 24px 20px;
            position: relative;
            display: flex;
            flex-direction: column;
            gap: 12px;

            ${mediaQueriesBP.maxLG} {
                padding: 20px 44px 20px 20px;
            }
        `,
    },
    detailTitle: {
        variant: "h3",
        as: "h3",
        css: css`
            margin: 0;
            padding-right: 8px;
            color: ${getColor(colorStrings.commonGlobalBlack)};
        `,
    },
    detailDescription: {
        size: 14,
        css: css`
            margin: 0;
            color: ${getColor(colorStrings.textMain)};
            white-space: pre-wrap;
            overflow-wrap: break-word;
            word-break: break-word;
            line-height: 1.5;
        `,
    },
    detailCta: {
        variant: "primary",
        css: css`
            align-self: flex-start;
            margin-top: 8px;
        `,
    },
    detailCtaLink: {
        css: css`
            align-self: flex-start;
            margin-top: 8px;
        `,
    },
    mobileList: {
        css: css`
            margin-top: 24px;
            display: flex;
            flex-direction: column;
            gap: 28px;

            ${mediaQueriesBP.minLG} {
                display: none;
            }
        `,
    },
    mobileListItem: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid ${getColor(colorStrings.commonGlobalGray03)};
            background: ${getColor(colorStrings.commonGlobalWhite)};
            scroll-margin-top: 16px;
        `,
    },
    mobileListHeader: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 16px;
            flex: 0 0 auto;
        `,
    },
    mobileListContent: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0;
            width: 100%;
            min-width: 0;
        `,
    },
    mobileListItemActive: {
        css: css`
            border-width: 2px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        `,
    },
    mobileListNumber: {
        size: 32,
        weight: 700,
        css: css`
            flex: 0 0 auto;
            line-height: 1;
            min-width: 1.2em;
        `,
    },
    mobileListTitle: {
        variant: "h4",
        as: "h3",
        css: css`
            margin: 0 0 8px;
            color: ${getColor(colorStrings.textMain)};
            width: 100%;
            overflow-wrap: break-word;
            word-break: break-word;
        `,
    },
    mobileListDescription: {
        size: 14,
        css: css`
            margin: 0 0 12px;
            color: ${getColor(colorStrings.textMain)};
            white-space: pre-wrap;
            overflow-wrap: break-word;
            word-break: break-word;
            width: 100%;
        `,
    },
    mobileListImage: {
        css: css`
            flex: 0 0 auto;
            width: 72px;
            min-width: 72px;

            img {
                width: 100%;
                height: auto;
                display: block;
                border-radius: 4px;
                object-fit: cover;
                aspect-ratio: 1;
            }
        `,
    },
    mobileListCta: {
        variant: "primary",
        css: css`
            align-self: flex-start;
            margin-top: 4px;
            max-width: 100%;
            white-space: normal;
            text-align: center;
        `,
    },
    mobileListCtaLink: {
        css: css`
            align-self: flex-start;
            margin-top: 4px;
            max-width: 100%;
            overflow-wrap: break-word;
            word-break: break-word;
        `,
    },
};

export const styles = interactivePhotoMapWidgetStyles;

const HotspotMarkerButton = styled.button<{
    $x: number;
    $y: number;
    $bg: string;
    $active: boolean;
    $isTextLabel: boolean;
    $modalOpen: boolean;
}>`
    position: absolute;
    left: ${p => p.$x}%;
    top: ${p => p.$y}%;
    transform: translate(-50%, -50%);
    z-index: ${p => (p.$modalOpen ? 1000 : 2)};
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
    padding: ${p => (p.$isTextLabel ? "8px 14px" : "0")};
    margin: 0;
    border-radius: ${p => (p.$isTextLabel ? "999px" : "50%")};
    cursor: pointer;
    border: 3px solid ${getColor(colorStrings.commonGlobalWhite)};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
    background-color: ${p => p.$bg};
    transition:
        transform 0.15s ease,
        box-shadow 0.15s ease;

    ${mediaQueriesBP.maxLG} {
        min-width: 44px;
        min-height: 44px;
        padding: ${p => (p.$isTextLabel ? "10px 16px" : "0")};
    }

    &:focus {
        outline: 2px solid ${getColor(colorStrings.primaryMain)};
        outline-offset: 2px;
    }

    ${p =>
        p.$active
            ? css`
                  box-shadow:
                      0 0 0 3px ${getColor(colorStrings.commonGlobalWhite)},
                      0 0 0 6px ${p.$bg};
                  transform: translate(-50%, -50%) scale(1.08);
              `
            : ""}

    &:hover {
        transform: translate(-50%, -50%) scale(${p => (p.$active ? 1.08 : 1.06)});
    }
`;

const navigateCta = (url: string | undefined, openInNewTab: boolean, isInShell: boolean, history: History) => {
    if (!url) {
        return;
    }

    if (openInNewTab) {
        if (isInShell && url.startsWith("http")) {
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
        return;
    }

    if (url.startsWith("http")) {
        if (isInShell) {
            return;
        }
        window.location.href = url;
    } else {
        history.push(url);
    }
};

const clampPercent = (value: number | null | undefined, fallback: number) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return fallback;
    }
    return Math.min(100, Math.max(0, value));
};

/** Half of marker width including border (40px + 6px border, active scale buffer). */
const HOTSPOT_MARKER_HALF_SIZE_PX = 28;
const HOTSPOT_PANEL_VISUAL_GAP_PX = 12;
const HOTSPOT_PANEL_OFFSET_PX = HOTSPOT_MARKER_HALF_SIZE_PX + HOTSPOT_PANEL_VISUAL_GAP_PX;
const PANEL_EDGE_PADDING_PX = 12;
const PANEL_MAX_WIDTH_PX = 520;
const PANEL_POINTER_SIZE_PX = 12;
/** Hotspots past this X% open the panel to the left of the marker. */
const PANEL_FLIP_LEFT_THRESHOLD = 58;

export type PanelPointerSide = "left" | "right";

export interface DetailPanelPosition {
    left: string;
    right: string;
    top: string;
    transform: string;
    maxWidth: string;
    pointerSide: PanelPointerSide;
    pointerOffsetPercent: number;
}

/**
 * Positions the desktop detail card beside the active hotspot (Southwire-style),
 * flipping to the left or right side depending on horizontal space.
 */
export const getHotspotMarkerHalfWidthPx = (label: string, isTextLabel: boolean) => {
    if (!isTextLabel) {
        return HOTSPOT_MARKER_HALF_SIZE_PX;
    }
    return Math.min(80, 20 + label.length * 3.5);
};

export const getDetailPanelPosition = (
    xPercent: number | null | undefined,
    yPercent: number | null | undefined,
    markerHalfWidthPx: number = HOTSPOT_MARKER_HALF_SIZE_PX,
): DetailPanelPosition => {
    const x = clampPercent(xPercent, 50);
    const y = clampPercent(yPercent, 50);
    const openToLeft = x >= PANEL_FLIP_LEFT_THRESHOLD;
    const edgeInset = PANEL_EDGE_PADDING_PX;
    const offset = markerHalfWidthPx + HOTSPOT_PANEL_VISUAL_GAP_PX;
    const pointerSide: PanelPointerSide = openToLeft ? "right" : "left";

    const availableSpace = openToLeft
        ? `calc(${x}% - ${offset + edgeInset}px)`
        : `calc(100% - ${x}% - ${offset + edgeInset}px)`;

    const maxWidth = `min(${PANEL_MAX_WIDTH_PX}px, ${availableSpace})`;

    const left = openToLeft
        ? "auto"
        : `clamp(${edgeInset}px, calc(${x}% + ${offset}px), calc(100% - ${edgeInset}px))`;

    const right = openToLeft
        ? `clamp(${edgeInset}px, calc(100% - ${x}% + ${offset}px), calc(100% - ${edgeInset}px))`
        : "auto";

    let top = `${y}%`;
    let transform = "translateY(-50%)";
    let pointerOffsetPercent = 50;

    if (y < 15) {
        top = `${Math.max(y, 8)}%`;
        transform = "translateY(0)";
        pointerOffsetPercent = 22;
    } else if (y > 85) {
        top = `${Math.min(y, 92)}%`;
        transform = "translateY(-100%)";
        pointerOffsetPercent = 78;
    }

    return {
        left,
        right,
        top,
        transform,
        maxWidth,
        pointerSide,
        pointerOffsetPercent,
    };
};

const getPanelPointerCss = (
    pointerSide: PanelPointerSide,
    pointerOffsetPercent: number,
    borderColor: string,
) => {
    const pointerTop = `${pointerOffsetPercent}%`;
    const pointerInset = PANEL_POINTER_SIZE_PX / 2;
    const pointerBorder = `2px solid ${borderColor}`;

    if (pointerSide === "left") {
        return css`
            &::before {
                content: "";
                position: absolute;
                top: ${pointerTop};
                left: -${pointerInset}px;
                width: ${PANEL_POINTER_SIZE_PX}px;
                height: ${PANEL_POINTER_SIZE_PX}px;
                background: ${getColor(colorStrings.commonGlobalWhite)};
                border-left: ${pointerBorder};
                border-bottom: ${pointerBorder};
                transform: translateY(-50%) rotate(45deg);
                box-shadow: -2px 2px 4px rgba(0, 0, 0, 0.06);
                z-index: 0;
                pointer-events: none;
            }
        `;
    }

    return css`
        &::before {
            content: "";
            position: absolute;
            top: ${pointerTop};
            right: -${pointerInset}px;
            width: ${PANEL_POINTER_SIZE_PX}px;
            height: ${PANEL_POINTER_SIZE_PX}px;
            background: ${getColor(colorStrings.commonGlobalWhite)};
            border-right: ${pointerBorder};
            border-top: ${pointerBorder};
            transform: translateY(-50%) rotate(45deg);
            box-shadow: 2px -2px 4px rgba(0, 0, 0, 0.06);
            z-index: 0;
            pointer-events: none;
        }
    `;
};

const hotspotHasLink = (link: LinkFieldValue | undefined) => !!(link && link.value);

const isTextHotspotLabel = (label: string) => label.length > 2 || !/^\d+$/.test(label);

export const getContentImageSizePercent = (size: ContentImageSize | undefined): number => {
    switch (size) {
        case "small":
            return 30;
        case "large":
            return 55;
        case "medium":
        default:
            return 42;
    }
};

interface HotspotCtaProps {
    displayType: CtaDisplayType | undefined;
    text: string;
    url: string | undefined;
    openInNewTab: boolean;
    isInShell: boolean;
    history: History;
    buttonStyles?: ButtonProps;
    linkStyles?: LinkPresentationProps;
}

const HotspotCta = ({
    displayType,
    text,
    url,
    openInNewTab,
    isInShell,
    history,
    buttonStyles = styles.detailCta,
    linkStyles = styles.detailCtaLink,
}: HotspotCtaProps) => {
    if (!text.trim() || !url) {
        return null;
    }

    if (displayType === "link") {
        return (
            <Link
                {...linkStyles}
                href={url}
                target={openInNewTab ? "_blank" : undefined}
                onClick={event => {
                    if (isInShell && url.startsWith("http")) {
                        event.preventDefault();
                        return;
                    }
                    if (!openInNewTab && !url.startsWith("http")) {
                        event.preventDefault();
                        navigateCta(url, false, isInShell, history);
                    }
                }}
            >
                {text}
            </Link>
        );
    }

    return (
        <Button {...buttonStyles} onClick={() => navigateCta(url, openInNewTab, isInShell, history)}>
            {text}
        </Button>
    );
};

const mobileLayoutQuery = "(max-width: 1023px)";

export const InteractivePhotoMapWidget = ({ fields }: Props) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const mobileItemRefs = useRef<Array<HTMLDivElement | null>>([]);
    const detailPanelRef = useRef<HTMLDivElement | null>(null);

    const scrollToMobileListItem = (index: number) => {
        if (typeof window === "undefined" || !window.matchMedia(mobileLayoutQuery).matches) {
            return;
        }
        requestAnimationFrame(() => {
            mobileItemRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
    };

    const hotspots = (fields.hotspots ?? []).slice(0, 25);
    const ctaLinks = useGetLinks(hotspots, h => h.fields.ctaLink);

    const desktopSrc = sanitizeCmsImageSrc(fields.desktopBackgroundImage);
    const mobileSrcRaw = sanitizeCmsImageSrc(fields.mobileBackgroundImage);
    const mobileSrc = mobileSrcRaw || desktopSrc;
    const altText = fields.imageAltText || translate("Interactive Photo Map");

    const setActiveHotspot = useCallback((index: number | null) => {
        setActiveIndex(index);
    }, []);

    useEffect(() => {
        if (activeIndex === null) {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setActiveHotspot(null);
            }
        };

        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (detailPanelRef.current?.contains(target)) {
                return;
            }
            if ((target as Element).closest?.('[data-test-selector^="photoMapHotspot_"]')) {
                return;
            }
            setActiveHotspot(null);
        };

        window.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown);
        };
    }, [activeIndex, setActiveHotspot]);

    if (!desktopSrc || hotspots.length === 0) {
        return null;
    }

    const activeHotspot = activeIndex !== null ? hotspots[activeIndex] : null;
    const activeBorderColor: string = activeHotspot?.fields.hotspotColor || "#e82011";
    const activeContentImageSize = getContentImageSizePercent(activeHotspot?.fields.contentImageSize);
    const activeHotspotLabel =
        activeHotspot != null
            ? String(activeHotspot.fields.hotspotLabel ?? "").trim() ||
              String((activeIndex ?? 0) + 1)
            : "";
    const activePanelPosition =
        activeHotspot != null
            ? getDetailPanelPosition(
                  activeHotspot.fields.positionXPercent,
                  activeHotspot.fields.positionYPercent,
                  getHotspotMarkerHalfWidthPx(
                      activeHotspotLabel,
                      isTextHotspotLabel(activeHotspotLabel),
                  ),
              )
            : null;
    const isModalOpen = activeIndex !== null;

    return (
        <StyledWrapper {...styles.root} data-test-selector="interactivePhotoMap">
            <StyledWrapper
                {...styles.imageStage}
                css={css`
                    ${styles.imageStage?.css}
                    ${isModalOpen
                        ? css`
                              position: relative;
                              z-index: 999;
                              overflow: visible;
                          `
                        : ""}
                `}
            >
                {mobileSrcRaw ? (
                    <>
                        <StyledWrapper {...styles.desktopImage}>
                            <Img src={desktopSrc} altText={altText} loading="lazy" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.mobileImage}>
                            <Img src={mobileSrc} altText={altText} loading="lazy" />
                        </StyledWrapper>
                    </>
                ) : (
                    <StyledWrapper {...styles.singleBackgroundImage}>
                        <Img src={desktopSrc} altText={altText} loading="lazy" />
                    </StyledWrapper>
                )}

                {hotspots.map((hotspot, index) => {
                    const x = clampPercent(hotspot.fields.positionXPercent, 50);
                    const y = clampPercent(hotspot.fields.positionYPercent, 50);
                    const color = hotspot.fields.hotspotColor || "#e82011";
                    const label =
                        hotspot.fields.hotspotLabel?.trim() ||
                        String(index + 1);
                    const isActive = activeIndex === index;
                    const isTextLabel = isTextHotspotLabel(label);

                    return (
                        <HotspotMarkerButton
                            type="button"
                            key={index}
                            data-test-selector={`photoMapHotspot_${index}`}
                            aria-pressed={isActive}
                            aria-label={`${label}. ${hotspot.fields.title}`}
                            $x={x}
                            $y={y}
                            $bg={color}
                            $active={isActive}
                            $isTextLabel={isTextLabel}
                            $modalOpen={isModalOpen}
                            onClick={event => {
                                event.stopPropagation();
                                if (activeIndex === index) {
                                    setActiveHotspot(null);
                                } else {
                                    setActiveHotspot(index);
                                    scrollToMobileListItem(index);
                                }
                            }}
                        >
                            <Typography
                                {...styles.hotspotLabelTypography}
                                css={css`
                                    ${styles.hotspotLabelTypography?.css}
                                    ${isTextLabel ? "white-space: nowrap;" : ""}
                                `}
                            >
                                {label}
                            </Typography>
                        </HotspotMarkerButton>
                    );
                })}

                {activeIndex !== null && activeHotspot && (
                    <>
                        <StyledWrapper
                            {...styles.backdrop}
                            as="button"
                            type="button"
                            onClick={() => setActiveHotspot(null)}
                            aria-label={translate("Close")}
                        />
                        <StyledWrapper
                            ref={detailPanelRef}
                            {...styles.detailPanel}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={`photo-map-panel-title-${activeIndex}`}
                            onClick={event => event.stopPropagation()}
                            css={css`
                                ${styles.detailPanel?.css}
                                ${activePanelPosition
                                    ? css`
                                          left: ${activePanelPosition.left};
                                          right: ${activePanelPosition.right};
                                          top: ${activePanelPosition.top};
                                          transform: ${activePanelPosition.transform};
                                          max-width: ${activePanelPosition.maxWidth};
                                      `
                                    : ""}
                                ${activePanelPosition
                                    ? getPanelPointerCss(
                                          activePanelPosition.pointerSide,
                                          activePanelPosition.pointerOffsetPercent,
                                          activeBorderColor,
                                      )
                                    : ""}
                            `}
                        >
                            <StyledWrapper
                                {...styles.detailPanelInner}
                                css={css`
                                    ${styles.detailPanelInner?.css}
                                    border: 2px solid ${activeBorderColor};
                                `}
                            >
                                <Button
                                    {...styles.detailCloseButton}
                                    onClick={() => setActiveHotspot(null)}
                                    aria-label={translate("Close")}
                                >
                                    <ButtonIcon src={X} />
                                </Button>
                                {activeHotspot.fields.contentImage ? (
                                    <StyledWrapper
                                        {...styles.detailImageWrapper}
                                        css={css`
                                            ${styles.detailImageWrapper?.css}
                                            flex: 0 0 ${activeContentImageSize}%;
                                            width: ${activeContentImageSize}%;
                                            max-width: ${activeContentImageSize}%;
                                            min-width: ${activeContentImageSize === 30 ? "100px" : "140px"};
                                        `}
                                    >
                                        <Img
                                            src={sanitizeCmsImageSrc(activeHotspot.fields.contentImage)}
                                            altText={activeHotspot.fields.title}
                                            loading="lazy"
                                        />
                                    </StyledWrapper>
                                ) : null}
                                <StyledWrapper {...styles.detailBody}>
                                    <Typography
                                        {...styles.detailTitle}
                                        id={`photo-map-panel-title-${activeIndex}`}
                                    >
                                        {activeHotspot.fields.title}
                                    </Typography>
                                    {activeHotspot.fields.description ? (
                                        <Typography {...styles.detailDescription}>
                                            {activeHotspot.fields.description}
                                        </Typography>
                                    ) : null}
                                    {hotspotHasLink(activeHotspot.fields.ctaLink) &&
                                    activeHotspot.fields.ctaText?.trim() ? (
                                        <HotspotCta
                                            displayType={activeHotspot.fields.ctaDisplayType}
                                            text={activeHotspot.fields.ctaText.trim()}
                                            url={ctaLinks.links[activeIndex]?.url as string | undefined}
                                            openInNewTab={activeHotspot.fields.openInNewTab}
                                            isInShell={isInShell ?? false}
                                            history={history}
                                        />
                                    ) : null}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                )}
            </StyledWrapper>

            <StyledWrapper {...styles.mobileList}>
                {hotspots.map((hotspot, index) => {
                    const isActive = activeIndex === index;
                    const color = hotspot.fields.hotspotColor || "#e82011";
                    const label =
                        hotspot.fields.hotspotLabel?.trim() ||
                        String(index + 1);
                    const url = ctaLinks.links[index]?.url as string | undefined;

                    return (
                        <StyledWrapper
                            key={`mobile-${index}`}
                            ref={el => {
                                mobileItemRefs.current[index] = el;
                            }}
                            {...styles.mobileListItem}
                            css={css`
                                ${styles.mobileListItem?.css}
                                ${isActive ? styles.mobileListItemActive?.css : ""}
                                ${isActive ? `border-color: ${color};` : ""}
                            `}
                        >
                            <StyledWrapper {...styles.mobileListHeader}>
                                <Typography
                                    {...styles.mobileListNumber}
                                    css={css`
                                        ${styles.mobileListNumber?.css}
                                        color: ${color};
                                        ${isTextHotspotLabel(label)
                                            ? css`
                                                  font-size: 20px;
                                                  max-width: 100%;
                                                  overflow-wrap: break-word;
                                                  word-break: break-word;
                                              `
                                            : ""}
                                    `}
                                >
                                    {label}
                                </Typography>
                                {hotspot.fields.contentImage ? (
                                    <StyledWrapper {...styles.mobileListImage}>
                                        <Img
                                            src={sanitizeCmsImageSrc(hotspot.fields.contentImage)}
                                            altText={hotspot.fields.title}
                                            loading="lazy"
                                        />
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                            <StyledWrapper {...styles.mobileListContent}>
                                <Typography {...styles.mobileListTitle}>{hotspot.fields.title}</Typography>
                                {hotspot.fields.description ? (
                                    <Typography {...styles.mobileListDescription}>
                                        {hotspot.fields.description}
                                    </Typography>
                                ) : null}
                                {hotspotHasLink(hotspot.fields.ctaLink) && hotspot.fields.ctaText?.trim() ? (
                                    <HotspotCta
                                        displayType={hotspot.fields.ctaDisplayType}
                                        text={hotspot.fields.ctaText.trim()}
                                        url={url}
                                        openInNewTab={hotspot.fields.openInNewTab}
                                        isInShell={isInShell ?? false}
                                        history={history}
                                        buttonStyles={styles.mobileListCta}
                                        linkStyles={styles.mobileListCtaLink}
                                    />
                                ) : null}
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, null)(InteractivePhotoMapWidget as any) as any,
    definition: {
        displayName: "Interactive Photo Map",
        group: "Verndale Box" as any,
        icon: "map",
        fieldDefinitions: [
            {
                name: fields.desktopBackgroundImage,
                displayName: "Desktop background image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                sortOrder: 0,
                tooltip:
                    "Use the image picker when possible. If the image fails to load, check the path for accidental trailing characters (; ,).",
            },
            {
                name: fields.mobileBackgroundImage,
                displayName: "Mobile background image (optional)",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
                sortOrder: 1,
            },
            {
                name: fields.imageAltText,
                displayName: "Image alt text (accessibility)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
                sortOrder: 2,
            },
            {
                name: fields.hotspots,
                displayName: "Hotspots (up to 25)",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Contextual",
                tooltip:
                    "Position values are percentages from the top-left of the image (0–100). Drag rows to reorder hotspots.",
                getDisplay: ({ fields: f }) =>
                    (f.title as string | undefined)?.trim() ||
                    (f.hotspotLabel as string | undefined)?.trim() ||
                    translate("Hotspot"),
                fieldDefinitions: [
                    {
                        name: fields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        sortOrder: 0,
                    },
                    {
                        name: fields.hotspotLabel,
                        displayName: "Number / label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 1,
                        isRequired: false,
                        tooltip:
                            "Optional. Leave blank to auto-number hotspots. Use short text labels for named markers.",
                    },
                    {
                        name: fields.hotspotColor,
                        displayName: "Hotspot color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "#e82011",
                        sortOrder: 2,
                    },
                    {
                        name: fields.positionXPercent,
                        displayName: "Horizontal position (%)",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                        min: 0,
                        max: 100,
                        isRequired: true,
                        sortOrder: 3,
                    },
                    {
                        name: fields.positionYPercent,
                        displayName: "Vertical position (%)",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                        min: 0,
                        max: 100,
                        isRequired: true,
                        sortOrder: 4,
                    },
                    {
                        name: fields.description,
                        displayName: "Short description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                        isRequired: false,
                        sortOrder: 5,
                    },
                    {
                        name: fields.contentImage,
                        displayName: "Content image (optional)",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: false,
                        sortOrder: 6,
                    },
                    {
                        name: fields.contentImageSize,
                        displayName: "Content image size",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "medium",
                        horizontal: true,
                        isRequired: false,
                        sortOrder: 7,
                        tooltip: "Controls how much horizontal space the image uses inside the desktop popup.",
                        options: [
                            {
                                displayName: "Small",
                                value: "small",
                            },
                            {
                                displayName: "Medium",
                                value: "medium",
                            },
                            {
                                displayName: "Large",
                                value: "large",
                            },
                        ],
                    },
                    {
                        name: fields.ctaText,
                        displayName: "CTA label (optional)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        sortOrder: 8,
                    },
                    {
                        name: fields.ctaLink,
                        displayName: "CTA link (optional)",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: false,
                        sortOrder: 9,
                    },
                    {
                        name: fields.ctaDisplayType,
                        displayName: "CTA display style",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "button",
                        horizontal: true,
                        isRequired: false,
                        sortOrder: 10,
                        options: [
                            {
                                displayName: "Button",
                                value: "button",
                            },
                            {
                                displayName: "Hyperlink",
                                value: "link",
                            },
                        ],
                    },
                    {
                        name: fields.openInNewTab,
                        displayName: "Open CTA in new tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isRequired: false,
                        sortOrder: 11,
                    },
                ],
                sortOrder: 3,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
